// Entity-Karten des Workspace (Designkonzept §5): ILO, TLA, AT.
// Gemeinsame Shell: navy-card, Haarlinie, Hover-Lift (translateY −2px).
// Fokus-Modus (§1, „das eigentliche Feature"): Klick auf eine Karte setzt
// sie als origin (Glow), verknüpfte Karten werden hit (Glow), der Rest
// dimmt. Der Parent (CourseWorkspace) rechnet den FocusState aus.

import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { AT, DetectedFlaw, ILO, TLA, WizardCatalog } from "@/api/client";
import WsBadge from "@/components/Workspace/Badge";
import { useAtFormatLabels, useModalityLabels } from "@/lib/labels";

export type FocusState = "none" | "origin" | "hit" | "dim";

const FOCUS_CLASS: Record<FocusState, string> = {
  none: "",
  origin: "border-ws-glow shadow-ws-glow",
  hit: "border-[rgba(77,124,254,.55)] shadow-ws-glow",
  dim: "opacity-30",
};

// Bloom-Stufennamen (revidierte Taxonomie) kommen aus i18n als Array.
function useBloomLevels(): string[] {
  const { t } = useTranslation();
  const levels = t("workspace.bloomLevels", { returnObjects: true });
  return Array.isArray(levels) ? (levels as string[]) : [];
}

function BloomBadge({ level }: { level?: number | null }) {
  const levels = useBloomLevels();
  if (level == null) return null;
  const name = levels[level - 1];
  return <WsBadge variant="bloom">K{level}{name ? ` · ${name}` : ""}</WsBadge>;
}

// Vagheits-Warnung (§2): niedriger Messbarkeits-Score = passives Verb.
const VAGUE_THRESHOLD = 0.6;

function flawVariant(severity?: string): "warn" | "err" {
  return severity === "critical" ? "err" : "warn";
}

interface CardShellProps {
  domId: string;
  focus: FocusState;
  onSelect: () => void;
  onKai: () => void;
  kaiTitle: string;
  /** ✎-Affordanz pro Karte: startet das Inline-Editieren des Kartentexts. */
  onEdit: () => void;
  editLabel?: string;
  children: React.ReactNode;
}

function CardShell({ domId, focus, onSelect, onKai, kaiTitle, onEdit, editLabel, children }: CardShellProps) {
  return (
    <div
      id={domId}
      onClick={onSelect}
      className={`group relative mb-3 cursor-pointer rounded-xl border border-ws-surface-border bg-ws-card p-3.5 px-4 transition-[transform,box-shadow,border-color,opacity] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-ws-card-hover ${FOCUS_CLASS[focus]}`}
    >
      {/* ✎-Bearbeiten: bei Hover sichtbar, startet Inline-Edit in der Karte
          (Franz 2026-08-15: Karten sollen direkt änderbar sein, nicht nur
          über den Editor-Umweg). */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        title={editLabel}
        className="absolute right-16 top-2.5 rounded-md border border-ws-surface-border px-2 py-0.5 text-[11.5px] font-semibold text-ws-surface-muted opacity-0 transition-[opacity,color,border-color] duration-150 hover:border-[rgba(77,124,254,.45)] hover:text-ca-surface-accent group-hover:opacity-100"
      >
        ✎ {editLabel}
      </button>
      {/* ✦-KAI-Button: bei Hover sichtbar, öffnet den Drawer mit Kontext (§5a) */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onKai();
        }}
        title={kaiTitle}
        className="absolute right-3 top-2.5 rounded-md border border-ws-surface-border px-2 py-0.5 text-[11.5px] font-semibold text-ws-surface-muted opacity-0 transition-[opacity,color,border-color] duration-150 hover:border-[rgba(77,124,254,.45)] hover:text-ca-surface-accent group-hover:opacity-100"
      >
        ✦ KAI
      </button>
      {children}
    </div>
  );
}

/** Inline-Texteditor für Kartentexte: Enter = speichern, Esc = abbrechen.
 *  PATCH ist partiell (Backend merged per exclude_unset), Fehler bleibt
 *  im Editor sichtbar statt still zu schließen. */
function InlineEditText({
  value,
  onSave,
  onCancel,
}: {
  value: string;
  onSave: (text: string) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const text = draft.trim();
    if (!text || text === value) {
      onCancel();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(text);
    } catch {
      setError(t("common.error"));
      setBusy(false);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <textarea
        value={draft}
        autoFocus
        disabled={busy}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void save();
          }
          if (e.key === "Escape") onCancel();
        }}
        className="w-full resize-y rounded-md border border-ws-surface-border bg-white px-3 py-2 text-[15px] leading-[1.6] text-ws-surface-ink outline-none focus:border-[rgba(77,124,254,.5)]"
        rows={Math.max(3, Math.ceil(draft.length / 60))}
      />
      {error && <p className="mt-1 text-xs text-ws-red">{error}</p>}
      <div className="mt-2 flex gap-2">
        <button
          onClick={() => void save()}
          disabled={busy}
          className="rounded-md bg-[#4D7CFE] px-3 py-1 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-40"
        >
          {busy ? t("common.saving") : t("common.save")}
        </button>
        <button
          onClick={onCancel}
          disabled={busy}
          className="rounded-md border border-ws-surface-border px-3 py-1 text-sm font-semibold text-ws-surface-muted transition hover:text-ws-surface-ink disabled:opacity-40"
        >
          {t("common.cancel")}
        </button>
      </div>
    </div>
  );
}

function FlawBadges({ flaws }: { flaws: DetectedFlaw[] }) {
  if (flaws.length === 0) return null;
  return (
    <>
      {flaws.map((f, i) => (
        <WsBadge key={i} variant={flawVariant(f.severity)}>
          {f.severity === "critical" ? "✕" : "⚠"} {f.description}
        </WsBadge>
      ))}
    </>
  );
}

const metaLabelClass =
  "text-[11px] font-semibold uppercase tracking-[0.08em] text-ws-surface-muted";
const linksClass = "mt-2.5 text-xs text-ws-surface-muted";

interface CardInteractionProps {
  focus: FocusState;
  onSelect: () => void;
  onKai: () => void;
  /** Inline-Edit des Haupttexts: partieller PATCH + Reload im Parent. */
  onSaveText: (text: string) => Promise<void>;
}

export function IloCard({
  ilo,
  linkedTlaCount,
  linkedAtCount,
  flaws,
  focus,
  onSelect,
  onKai,
  onSaveText,
}: {
  ilo: ILO;
  linkedTlaCount: number;
  linkedAtCount: number;
  flaws: DetectedFlaw[];
} & CardInteractionProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const vague =
    ilo.measurability_score != null && ilo.measurability_score < VAGUE_THRESHOLD;
  return (
    <CardShell
      domId={`wscard-${ilo.id}`}
      focus={focus}
      onSelect={onSelect}
      onKai={onKai}
      onEdit={() => setEditing(true)}
      editLabel={t("workspace.editColumn")}
      kaiTitle={t("workspace.kai.titleIlo")}
    >
      {editing ? (
        <InlineEditText
          value={ilo.text}
          onSave={async (text) => {
            await onSaveText(text);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <div
          onDoubleClick={(e) => {
            e.stopPropagation();
            setEditing(true);
          }}
          className="pr-36 text-[15px] font-semibold leading-[1.6] tracking-[-0.01em] text-ws-surface-ink"
        >
          {ilo.text}
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <BloomBadge level={ilo.bloom_level} />
        {ilo.bloom_domain && (
          <WsBadge>{t(`workspace.domain.${ilo.bloom_domain}`)}</WsBadge>
        )}
        {vague && (
          <WsBadge variant="warn">⚠ {t("workspace.vagueVerb")}</WsBadge>
        )}
        {linkedAtCount === 0 && (
          <WsBadge variant="orphan">◌ {t("workspace.orphanIlo")}</WsBadge>
        )}
        <FlawBadges flaws={flaws} />
      </div>
      {ilo.content_area && (
        <div className={linksClass}>
          {t("workspace.condition")}: <b className="font-semibold text-ws-surface-ink">{ilo.content_area}</b>
        </div>
      )}
      {linkedTlaCount > 0 && (
        <div className={linksClass}>
          {t("workspace.linkedTlas", { count: linkedTlaCount })} ·{" "}
          {t("workspace.linkedAts", { count: linkedAtCount })}{" "}
          {t("workspace.linkedSuffix")}
        </div>
      )}
    </CardShell>
  );
}

export function TlaCard({
  tla,
  linkedIloNumbers,
  flaws,
  catalog,
  focus,
  onSelect,
  onKai,
  onSaveText,
}: {
  tla: TLA;
  linkedIloNumbers: number[];
  flaws: DetectedFlaw[];
  catalog: WizardCatalog | null;
} & CardInteractionProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const attachments = tla.extras?.attachments ?? [];
  const modalityLabels = useModalityLabels();
  const typeLabel =
    catalog?.tla_types.find((ty) => ty.id === tla.type)?.name ?? tla.type;
  return (
    <CardShell
      domId={`wscard-${tla.id}`}
      focus={focus}
      onSelect={onSelect}
      onKai={onKai}
      onEdit={() => setEditing(true)}
      editLabel={t("workspace.editColumn")}
      kaiTitle={t("workspace.kai.titleTla")}
    >
      {typeLabel && <div className={metaLabelClass}>{typeLabel}</div>}
      {editing ? (
        <InlineEditText
          value={tla.description || ""}
          onSave={async (text) => {
            await onSaveText(text);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <div
          onDoubleClick={(e) => {
            e.stopPropagation();
            setEditing(true);
          }}
          className="pr-36 text-[15px] font-semibold tracking-[-0.01em] text-ws-surface-ink"
        >
          {tla.description || typeLabel}
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {tla.duration_minutes != null && (
          <WsBadge>{tla.duration_minutes} min</WsBadge>
        )}
        {tla.modality && (
          <WsBadge>{modalityLabels[tla.modality] ?? tla.modality}</WsBadge>
        )}
        {linkedIloNumbers.length === 0 && (
          <WsBadge variant="orphan">◌ {t("workspace.orphanTla")}</WsBadge>
        )}
        {attachments.length > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowAttachments((v) => !v);
            }}
            title={t("workspace.attachments")}
            className="rounded-full border border-ws-surface-border px-2 py-0.5 text-[11px] font-semibold text-ws-surface-muted transition hover:border-[rgba(77,124,254,.45)] hover:text-ca-surface-accent"
          >
            📎 {attachments.length}
          </button>
        )}
        <FlawBadges flaws={flaws} />
      </div>
      {showAttachments && attachments.length > 0 && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="mt-2.5 space-y-2 border-t border-dashed border-ws-surface-border pt-2"
        >
          {attachments.map((a, ai) => (
            <details key={ai} className="rounded-md bg-white p-3">
              <summary className="cursor-pointer text-sm font-semibold text-ws-surface-ink">
                📎 {a.title}
              </summary>
              <div className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-ws-surface-ink">
                {a.content.replace(/\*\*/g, "").replace(/^#{1,3}\s?/gm, "").replace(/^---$/gm, "")}
              </div>
            </details>
          ))}
        </div>
      )}
      {linkedIloNumbers.length > 0 && (
        <div className={linksClass}>
          {t("workspace.preparesFor")}:{" "}
          <b className="font-semibold text-ws-surface-ink">
            {linkedIloNumbers.map((n) => `ILO ${n}`).join(", ")}
          </b>
        </div>
      )}
      {/* Insight-Zone (Erweiterungsreserve, Konzept §5.2): Slot ist jetzt mit
          dem Bloom-Bezug belegt, später neurodidaktische Hinweise. */}
      {tla.bloom_levels && tla.bloom_levels.length > 0 && (
        <div className="mt-2.5 border-t border-dashed border-ws-surface-border pt-2 text-[12.5px] text-ws-surface-muted">
          {t("workspace.insightBloom", {
            levels: tla.bloom_levels.map((l) => `K${l}`).join("–"),
          })}
        </div>
      )}
    </CardShell>
  );
}

export function AtCard({
  at,
  linkedIloNumbers,
  flaws,
  catalog,
  focus,
  onSelect,
  onKai,
  onSaveText,
}: {
  at: AT;
  linkedIloNumbers: number[];
  flaws: DetectedFlaw[];
  catalog: WizardCatalog | null;
} & CardInteractionProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const atFormatLabels = useAtFormatLabels();
  const typeLabel =
    catalog?.at_types.find((ty) => ty.id === at.type)?.name ?? at.type;
  const maxBloom = at.bloom_levels?.length ? Math.max(...at.bloom_levels) : null;
  return (
    <CardShell
      domId={`wscard-${at.id}`}
      focus={focus}
      onSelect={onSelect}
      onKai={onKai}
      onEdit={() => setEditing(true)}
      editLabel={t("workspace.editColumn")}
      kaiTitle={t("workspace.kai.titleAt")}
    >
      {typeLabel && <div className={metaLabelClass}>{typeLabel}</div>}
      {editing ? (
        <InlineEditText
          value={at.description || ""}
          onSave={async (text) => {
            await onSaveText(text);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <div
          onDoubleClick={(e) => {
            e.stopPropagation();
            setEditing(true);
          }}
          className="pr-36 text-[15px] font-semibold tracking-[-0.01em] text-ws-surface-ink"
        >
          {at.description || typeLabel}
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {at.format && (
          <WsBadge>{atFormatLabels[at.format] ?? at.format}</WsBadge>
        )}
        {at.weight != null && <WsBadge>{Math.round(at.weight * 100)} %</WsBadge>}
        <BloomBadge level={maxBloom} />
        {linkedIloNumbers.length === 0 && (
          <WsBadge variant="orphan">◌ {t("workspace.orphanAt")}</WsBadge>
        )}
        <FlawBadges flaws={flaws} />
      </div>
      {linkedIloNumbers.length > 0 && (
        <div className={linksClass}>
          {t("workspace.assesses")}:{" "}
          <b className="font-semibold text-ws-surface-ink">
            {linkedIloNumbers.map((n) => `ILO ${n}`).join(", ")}
          </b>
        </div>
      )}
    </CardShell>
  );
}
